import api from '@/services/api'
import type {
  AprovarPoliticaRequest,
  PoliticaPendente,
  PoliticaVigenteProduto,
  RejeitarPoliticaRequest,
} from '@/types/politicaProduto'

/**
 * P6 (AN-PP6 §2.4/§3.4) — leitura agregada da política do produto e fila de aprovação.
 *
 * Leitura em `produtos` (projeção; nenhuma chamada síncrona entre serviços). As ações de aprovar/rejeitar moram no
 * módulo DONO de cada política (simulação, oportunidades, comissões, produtos); a fila traz a rota de cada ação e a
 * tela a usa como veio (`linkAprovar`/`linkRejeitar`) — só aceita caminhos da própria API (`/api/v1/…`), nunca outro host.
 * Os controllers devolvem o DTO direto e o interceptor de `api` já entrega o corpo.
 */

const PREFIXO_API = '/api/v1/'

/** Aceita só rota relativa da própria API (sem esquema, sem `//`, sem `..`). */
export const rotaDeAcaoValida = (rota: string | null | undefined): rota is string =>
  typeof rota === 'string' && rota.startsWith(PREFIXO_API) && !rota.includes('..') && !rota.includes('//')

export const politicaProdutoService = {
  /** `GET /v1/produtos/{codigo}/politica?vigenteEm=` — sem `vigenteEm`, o servidor usa hoje (America/Sao_Paulo). */
  vigente: async (codigo: string, vigenteEm?: string | null): Promise<PoliticaVigenteProduto> => {
    const r = (await api.get(`/api/v1/produtos/${encodeURIComponent(codigo)}/politica`, {
      params: vigenteEm ? { vigenteEm } : undefined,
    })) as unknown as PoliticaVigenteProduto
    return {
      ...r,
      componentes: r?.componentes ?? {},
      ausentes: r?.ausentes ?? [],
      pendentesAprovacao: r?.pendentesAprovacao ?? [],
      sincronizacao: r?.sincronizacao ?? {},
    }
  },

  /** `GET /v1/produtos/politicas/pendentes` (OPERADOR+ back-office). Aceita lista direta ou página `{content}`. */
  pendentes: async (): Promise<PoliticaPendente[]> => {
    const r = (await api.get('/api/v1/produtos/politicas/pendentes')) as unknown
    if (Array.isArray(r)) return r as PoliticaPendente[]
    const content = (r as { content?: unknown } | null)?.content
    return Array.isArray(content) ? (content as PoliticaPendente[]) : []
  },

  /** `POST {linkAprovar}` no módulo dono. 403 `APROVACAO_PELO_PROPONENTE`, 409 `CONFLITO_VERSAO`/`VIGENCIA_EXPIRADA_NA_APROVACAO`. */
  aprovar: async (rota: string, request: AprovarPoliticaRequest = {}): Promise<unknown> => {
    if (!rotaDeAcaoValida(rota)) throw { message: 'Rota de aprovação inválida recebida do servidor.' }
    return api.post(rota, request)
  },

  /** `POST {linkRejeitar}` no módulo dono, com motivo obrigatório (422 sem motivo). */
  rejeitar: async (rota: string, request: RejeitarPoliticaRequest): Promise<unknown> => {
    if (!rotaDeAcaoValida(rota)) throw { message: 'Rota de rejeição inválida recebida do servidor.' }
    return api.post(rota, request)
  },

  /**
   * `POST {linkRetirar}` no módulo dono (revisão PP6-valr, 8b): o PRÓPRIO proponente retira a proposta (403
   * `RETIRADA_SO_PELO_PROPONENTE` para qualquer outra pessoa).
   */
  retirar: async (rota: string): Promise<unknown> => {
    if (!rotaDeAcaoValida(rota)) throw { message: 'Rota de retirada inválida recebida do servidor.' }
    return api.post(rota, {})
  },
}

// ─── Erros (envelope `RequisicaoFalhou` de `api`: `data` é o corpo de erro do backend) ───

export const codigoErroPolitica = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

export const statusErroPolitica = (e: unknown): number | undefined => {
  const s = (e as { status?: unknown } | null)?.status
  return typeof s === 'number' ? s : undefined
}

/**
 * O erro como o backend o mandou: a mensagem dele e o código entre parênteses. A tela não reescreve a regra
 * (403 `APROVACAO_PELO_PROPONENTE`, 422 `VIGENTE_EM_FUTURO`, 404 `PRODUTO_NAO_ENCONTRADO`…); `padrao` só quando
 * não veio mensagem nenhuma (rede, timeout).
 */
export const mensagemErroPolitica = (e: unknown, padrao: string): string => {
  const codigo = codigoErroPolitica(e)
  const bruta = (e as { message?: unknown } | null)?.message
  const mensagem = typeof bruta === 'string' && bruta.trim() ? bruta.trim() : padrao
  return codigo && !mensagem.includes(codigo) ? `${mensagem} (${codigo})` : mensagem
}
